import React, { useState, useCallback, useRef, useEffect } from 'react';
import { AutoComplete, Tooltip } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { shelfService } from '@services/operator/shelfService';
import { useTranslation } from 'react-i18next';

const DEBOUNCE_MS = 200;

export default function ShelfAutocomplete({ value, warehouseId, onChange, disabled, style }) {
  const { t } = useTranslation();
  const [options, setOptions] = useState([]);
  const [validation, setValidation] = useState({ level: 'ok' });
  const debounceRef = useRef(null);

  const runSearch = useCallback(async (prefix) => {
    if (!prefix) { setOptions([]); setValidation({ level: 'ok' }); return; }
    if (!warehouseId) { setOptions([]); setValidation({ level: 'ok' }); return; }
    try {
      const results = await shelfService.search({
        locationPrefix: prefix, warehouseId, role: 'storage', limit: 10
      });
      setOptions(results.map(s => ({ value: s.location, shelfId: s.id, role: s.role })));
      setValidation({ level: 'ok' });
    } catch (e) {
      // 網路/5xx 失敗：清空選項 + 標示 lookup_failed，避免與「真的沒有這個儲位」混淆。
      setOptions([]);
      setValidation({ level: 'error', code: 'lookup_failed' });
    }
  }, [warehouseId]);

  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  const handleSearch = (text) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(text), DEBOUNCE_MS);
  };

  const handleSelect = (loc, opt) => {
    onChange?.({ shelfId: opt.shelfId, location: loc });
    setValidation({ level: 'ok' });
  };

  const handleBlur = async () => {
    // Free-typed location (no select) — validate against API
    if (!value) { setValidation({ level: 'ok' }); return; }
    const match = options.find(o => o.value === value);
    if (match) {
      onChange?.({ shelfId: match.shelfId, location: value });
      setValidation({ level: 'ok' });
      return;
    }
    try {
      const results = await shelfService.search({ locationPrefix: value, warehouseId, role: 'storage', limit: 1 });
      const exact = results.find(r => r.location === value);
      if (exact) {
        onChange?.({ shelfId: exact.id, location: value });
        setValidation({ level: 'ok' });
      } else {
        onChange?.({ shelfId: null, location: value });
        setValidation({ level: 'error', code: 'wrong_warehouse_or_role' });
      }
    } catch (e) {
      // 查詢失敗必須把 shelfId 清掉，避免送出過期/錯倉的值
      onChange?.({ shelfId: null, location: value });
      setValidation({ level: 'error', code: 'lookup_failed' });
    }
  };

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, width: 180, ...style }}>
      <AutoComplete
        value={value || ''}
        options={options}
        onSearch={handleSearch}
        onChange={(v) => onChange?.({ shelfId: null, location: v })}
        onSelect={handleSelect}
        onBlur={handleBlur}
        disabled={disabled}
        placeholder={t('operator.pages.putaway.placeholder.shelf')}
        style={{ flex: 1, minWidth: 0 }}
      />
      {validation.level === 'error' && (
        <Tooltip title={t(`operator.pages.putaway.warnings.${validation.code}`)}>
          <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
        </Tooltip>
      )}
    </span>
  );
}
